import React from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import { Link } from 'react-router-dom';
import { Clock, Car, CreditCard, FileText, ArrowRight, MapPin, CheckCircle, Home, Users } from 'lucide-react';
import EmergencyBailAssistanceForm from '../landing-page/components/EmergencyBailAssistanceForm';

const Services = () => {
  // Constants
  const EMERGENCY_PHONE = '(916) 256-3468';
  const OFFICE_ADDRESS = '901 H Street, Suite 103, Sacramento, CA 95814';

  // Service categories with detailed information
  const services = [
    {
      id: 'emergency-bail',
      icon: Clock,
      title: 'Emergency Bail Bonds',
      color: 'from-red-500 to-red-600',
      borderColor: 'border-red-300',
      description: 'Available 24/7 with 2-minute average response time. We understand every situation is unique and treat each case with urgency and care.',
      features: [
        'Immediate agent response',
        'Same-day release processing',
        'All California counties covered',
        'Direct jail liaison'
      ],
      pricing: 'Standard 10% premium rate',
      timeline: 'Processing: 2-6 hours average'
    },
    {
      id: 'csap-program',
      icon: Users,
      title: 'Client Success & Accountability Program',
      color: 'from-[#007BFF] to-blue-600',
      borderColor: 'border-blue-300',
      description: 'A comprehensive post-release support system built into our bail services. Weekly check-ins, compliance tracking, family support, and life stabilization to reduce recidivism and ensure court compliance.',
      features: [
        'Weekly accountability check-ins',
        'Compliance monitoring & tracking',
        'Family support & communication',
        'Court-prepared progress reports',
        'Behavioral improvement coaching',
        'Resource navigation assistance'
      ],
      pricing: 'Included with bail service',
      timeline: 'Enrollment: Immediate upon release',
      ctaText: 'Learn About CSAP',
      ctaLink: '/client-success-accountability-program-csap'
    },
    {
      id: 'payment-plans',
      icon: CreditCard,
      title: 'Flexible Payment Plans',
      color: 'from-[#28A745] to-green-600',
      borderColor: 'border-green-300',
      description: 'No judgment, just help when you need it most. We work with your budget to create payment plans that fit your financial situation.',
      features: [
        'Low down payments available',
        '0% interest options',
        'Extended payment terms',
        'No hidden fees'
      ],
      pricing: 'Custom plans from $50/month',
      timeline: 'Approval: Same day'
    },
    {
      id: 'transportation',
      icon: Car,
      title: 'Jail-to-Home Transportation',
      color: 'from-indigo-500 to-indigo-600',
      borderColor: 'border-indigo-300',
      description: 'A rare service you won\'t find elsewhere. We pick your loved one up from jail and bring them home safely—no need to arrange rides.',
      features: [
        'Direct jail pickup',
        'Safe transport to home',
        'Professional drivers',
        'Included with bail bond'
      ],
      pricing: 'Included with service',
      timeline: 'Pickup: Within 1 hour of release'
    },
    {
      id: 'bail-guidance',
      icon: FileText,
      title: 'Bail Process Guidance',
      color: 'from-purple-500 to-purple-600',
      borderColor: 'border-purple-300',
      description: 'Got questions? We\'ll explain payment options in plain English to fit your needs. No confusing jargon—just clear, straightforward guidance.',
      features: [
        'Step-by-step process explanation',
        'Legal requirements clarification',
        'Court date coordination',
        'Document assistance'
      ],
      pricing: 'Free consultation',
      timeline: 'Available: 24/7 by phone'
    }
  ];

  // Service comparison data
  const comparison = [
    {
      feature: '24/7 Availability',
      emergency: true,
      standard: true,
      payment: true
    },
    {
      feature: 'Jail Transportation',
      emergency: true,
      standard: false,
      payment: false
    },
    {
      feature: 'Payment Plans',
      emergency: true,
      standard: true,
      payment: true
    },
    {
      feature: 'Same-Day Processing',
      emergency: true,
      standard: true,
      payment: false
    },
    {
      feature: 'All Counties',
      emergency: true,
      standard: true,
      payment: true
    }
  ];

  // County-specific information
  const counties = [
    {
      name: 'Sacramento County',
      facilities: ['Main Jail', 'Rio Consumnes Correctional Center'],
      avgTime: '3-4 hours',
      coverage: '24/7 immediate response'
    },
    {
      name: 'Placer County',
      facilities: ['Placer County Jail (Auburn)', 'South Placer Jail (Roseville)'],
      avgTime: '4-5 hours',
      coverage: '24/7 immediate response'
    },
    {
      name: 'El Dorado County',
      facilities: ['El Dorado County Jail (Placerville)', 'South Lake Tahoe Jail'],
      avgTime: '5-6 hours',
      coverage: '24/7 immediate response'
    },
    {
      name: 'Yuba County',
      facilities: ['Yuba County Jail (Marysville)'],
      avgTime: '4-5 hours',
      coverage: '24/7 immediate response'
    },
    {
      name: 'Yolo County',
      facilities: ['Yolo County Jail (Woodland)', 'Leinberger Detention Facility'],
      avgTime: '4-5 hours',
      coverage: '24/7 immediate response'
    },
    {
      name: 'San Joaquin County',
      facilities: ['County Jail', 'Honor Farm'],
      avgTime: '4-5 hours',
      coverage: '24/7 immediate response'
    },
    {
      name: 'Stanislaus County',
      facilities: ['Public Safety Center', 'Downtown Jail'],
      avgTime: '4-6 hours',
      coverage: '24/7 immediate response'
    }
  ];

  return (
    <>
      <Helmet>
        <title>Bail Bond Services Sacramento | 24/7 Emergency Help | Sailors Bail Bonds</title>
        <meta 
          name="description" 
          content="Comprehensive bail bond services in Sacramento, Stockton, and Modesto. Emergency bonds, payment plans, jail transportation, and bail guidance. Available 24/7. Call (916) 256-3468." 
        />
        <meta 
          name="keywords" 
          content="bail bond services, emergency bail, payment plans, jail transportation, Sacramento bail, California bail bonds" 
        />
      </Helmet>

      <div className="min-h-screen bg-white">
        <Header />
        
        {/* Breadcrumbs */}
        <nav className="pt-32 pb-4 px-4 sm:px-6 lg:px-8 bg-white border-b" aria-label="Breadcrumb">
          <div className="max-w-7xl mx-auto">
            <ol className="flex items-center space-x-2 text-sm" style={{ fontFamily: 'Open Sans, sans-serif' }}>
              <li>
                <Link to="/" className="text-[#007BFF] hover:text-blue-700 transition-colors">
                  Home
                </Link>
              </li>
              <li className="text-gray-400">/</li>
              <li className="text-gray-900 font-semibold">Services</li>
            </ol>
          </div>
        </nav>

        {/* Hero Section */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#007BFF] via-blue-600 to-blue-700">
          <div className="max-w-7xl mx-auto text-center">
            <h2 
              className="text-5xl lg:text-6xl font-black text-white mb-6 leading-tight"
              style={{ fontFamily: 'Roboto Slab, serif' }}
            >
              Bail Bond Services You Can Trust
            </h2>
            <p 
              className="text-2xl text-blue-100 leading-relaxed max-w-4xl mx-auto"
              style={{ fontFamily: 'Open Sans, sans-serif' }}
            >
              We understand how overwhelming bail can feel—our team is here to chat you through it with kindness and clarity.
            </p>
          </div>
        </section>

        {/* Service Grid */}
        <section className="py-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12">
              {services?.map((service) => {
                const IconComponent = service?.icon;
                return (
                  <div 
                    key={service?.id}
                    className={`bg-white border-2 ${service?.borderColor} rounded-3xl p-10 shadow-lg hover:shadow-2xl transition-all`}
                  >
                    {/* Service Icon & Title */}
                    <div className="flex items-center space-x-4 mb-6">
                      <div className={`bg-gradient-to-br ${service?.color} w-20 h-20 rounded-2xl flex items-center justify-center`}>
                        <IconComponent className="w-10 h-10 text-white" />
                      </div>
                      <h3 
                        className="text-3xl font-black text-gray-900"
                        style={{ fontFamily: 'Roboto Slab, serif' }}
                      >
                        {service?.title}
                      </h3>
                    </div>

                    {/* Description */}
                    <p 
                      className="text-lg text-gray-700 leading-relaxed mb-6"
                      style={{ fontFamily: 'Open Sans, sans-serif' }}
                    >
                      {service?.description}
                    </p>

                    {/* Features List */}
                    <div className="mb-6">
                      <h4 className="text-xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Roboto Slab, serif' }}>
                        What's Included:
                      </h4>
                      <ul className="space-y-3">
                        {service?.features?.map((feature, idx) => (
                          <li key={idx} className="flex items-start space-x-3">
                            <CheckCircle className="w-6 h-6 text-[#28A745] flex-shrink-0 mt-0.5" />
                            <span className="text-base text-gray-700" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                              {feature}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Pricing & Timeline */}
                    <div className="bg-[#F8F9FA] rounded-2xl p-6 space-y-3 mb-6">
                      <div className="flex items-center justify-between">
                        <span className="text-base font-semibold text-gray-600" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                          Pricing:
                        </span>
                        <span className="text-lg font-bold text-gray-900" style={{ fontFamily: 'Roboto Slab, serif' }}>
                          {service?.pricing}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-base font-semibold text-gray-600" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                          Timeline:
                        </span>
                        <span className="text-lg font-bold text-gray-900" style={{ fontFamily: 'Roboto Slab, serif' }}>
                          {service?.timeline}
                        </span>
                      </div>
                    </div>

                    {/* CTA Button */}
                    {service?.ctaLink ? (
                      <Link 
                        to={service?.ctaLink}
                        className={`w-full bg-gradient-to-r ${service?.color} text-white px-8 py-4 rounded-xl font-black text-lg hover:shadow-xl transition-all transform hover:scale-105 flex items-center justify-center space-x-2`}
                      >
                        <span>{service?.ctaText || 'Get Started Now'}</span>
                        <ArrowRight className="w-6 h-6" />
                      </Link>
                    ) : (
                      <a 
                        href="#emergency-form"
                        className={`w-full bg-gradient-to-r ${service?.color} text-white px-8 py-4 rounded-xl font-black text-lg hover:shadow-xl transition-all transform hover:scale-105 flex items-center justify-center space-x-2`}
                      >
                        <span>Get Started Now</span>
                        <ArrowRight className="w-6 h-6" />
                      </a>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Service Comparison Table */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h3 
                className="text-4xl font-black text-gray-900 mb-4"
                style={{ fontFamily: 'Roboto Slab, serif' }}
              >
                Compare Our Services
              </h3>
              <p 
                className="text-xl text-gray-600"
                style={{ fontFamily: 'Open Sans, sans-serif' }}
              >
                Choose the service that best fits your needs
              </p>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full border-2 border-gray-200 rounded-2xl overflow-hidden">
                <thead className="bg-gradient-to-r from-[#007BFF] to-blue-600">
                  <tr>
                    <th className="px-6 py-4 text-left text-white font-bold text-lg" style={{ fontFamily: 'Roboto Slab, serif' }}>
                      Feature
                    </th>
                    <th className="px-6 py-4 text-center text-white font-bold text-lg" style={{ fontFamily: 'Roboto Slab, serif' }}>
                      Emergency Bail
                    </th>
                    <th className="px-6 py-4 text-center text-white font-bold text-lg" style={{ fontFamily: 'Roboto Slab, serif' }}>
                      Standard Service
                    </th>
                    <th className="px-6 py-4 text-center text-white font-bold text-lg" style={{ fontFamily: 'Roboto Slab, serif' }}>
                      Payment Plans
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {comparison?.map((row, idx) => (
                    <tr key={idx} className={idx % 2 === 0 ? 'bg-[#F8F9FA]' : 'bg-white'}>
                      <td className="px-6 py-4 text-base font-semibold text-gray-900" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                        {row?.feature}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {row?.emergency ? (
                          <CheckCircle className="w-8 h-8 text-[#28A745] mx-auto" />
                        ) : (
                          <span className="text-gray-400 text-2xl">—</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {row?.standard ? (
                          <CheckCircle className="w-8 h-8 text-[#28A745] mx-auto" />
                        ) : (
                          <span className="text-gray-400 text-2xl">—</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {row?.payment ? (
                          <CheckCircle className="w-8 h-8 text-[#28A745] mx-auto" />
                        ) : (
                          <span className="text-gray-400 text-2xl">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* County-Specific Information */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-50 to-[#F8F9FA]">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h3 
                className="text-4xl font-black text-gray-900 mb-4"
                style={{ fontFamily: 'Roboto Slab, serif' }}
              >
                Coverage by County
              </h3>
              <p 
                className="text-xl text-gray-600"
                style={{ fontFamily: 'Open Sans, sans-serif' }}
              >
                Comprehensive coverage across Northern California counties
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {counties?.map((county) => (
                <div 
                  key={county?.name}
                  className="bg-white border-2 border-[#007BFF]/20 rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all"
                >
                  <div className="flex items-center space-x-3 mb-6">
                    <MapPin className="w-8 h-8 text-[#007BFF]" />
                    <h4 
                      className="text-2xl font-black text-gray-900"
                      style={{ fontFamily: 'Roboto Slab, serif' }}
                    >
                      {county?.name}
                    </h4>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <p className="text-sm font-semibold text-gray-600 mb-2" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                        Facilities Served:
                      </p>
                      <ul className="space-y-1">
                        {county?.facilities?.map((facility, idx) => (
                          <li key={idx} className="text-base text-gray-700 flex items-center space-x-2">
                            <CheckCircle className="w-4 h-4 text-[#28A745]" />
                            <span>{facility}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div>
                      <p className="text-sm font-semibold text-gray-600 mb-1" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                        Average Release Time:
                      </p>
                      <p className="text-lg font-bold text-[#007BFF]" style={{ fontFamily: 'Roboto Slab, serif' }}>
                        {county?.avgTime}
                      </p>
                    </div>

                    <div>
                      <p className="text-sm font-semibold text-gray-600 mb-1" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                        Coverage:
                      </p>
                      <p className="text-base font-bold text-[#28A745]" style={{ fontFamily: 'Roboto Slab, serif' }}>
                        {county?.coverage}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Emergency Form Section */}
        <section id="emergency-form" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h3 
                className="text-4xl font-black text-gray-900 mb-4"
                style={{ fontFamily: 'Roboto Slab, serif' }}
              >
                Start Your Bail Process Now
              </h3>
              <p 
                className="text-xl text-gray-600"
                style={{ fontFamily: 'Open Sans, sans-serif' }}
              >
                Fill out this form and a licensed agent will contact you within 2 minutes
              </p>
            </div>

            <EmergencyBailAssistanceForm />
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default Services;